import { useState } from "react";
import { Icon } from "./Icon";
import { StatusPill } from "./primitives";
import { useSnapshot } from "@/lib/snapshot-context";
import type { Incident, IncidentStatus } from "@/lib/types";

interface Props {
  incident: Incident;
  collapsed?: boolean;
}

const STATUS_RING: Record<IncidentStatus, string> = {
  investigating: "var(--destructive)",
  identified: "var(--warning)",
  monitoring: "var(--info)",
  resolved: "var(--success)",
};

const STATUS_LABEL_COLOR: Record<IncidentStatus, string> = {
  investigating: "var(--destructive)",
  identified: "color-mix(in oklch, var(--warning) 60%, #000)",
  monitoring: "var(--info)",
  resolved: "var(--success)",
};

export function IncidentDetail({ incident, collapsed }: Props) {
  const { fmt } = useSnapshot();
  const [open, setOpen] = useState(!collapsed);
  const sevColor =
    incident.severity === "major" ? "var(--destructive)" : "var(--warning)";
  const isActive = incident.status !== "resolved";

  return (
    <div style={{ borderTop: "1px solid var(--border)" }}>
      <div
        role="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          padding: "14px 16px",
          display: "flex",
          gap: 12,
          cursor: "pointer",
          alignItems: "center",
        }}
      >
        <span
          style={{
            width: 4,
            alignSelf: "stretch",
            background: sevColor,
            borderRadius: 2,
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 500 }}>{incident.title}</span>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                height: 18,
                padding: "0 7px",
                borderRadius: 4,
                fontSize: 10,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                background: `color-mix(in oklch, ${sevColor} 14%, transparent)`,
                color:
                  sevColor === "var(--destructive)"
                    ? sevColor
                    : "color-mix(in oklch, var(--warning) 60%, #000)",
              }}
            >
              {incident.severity}
            </span>
            {isActive && (
              <StatusPill
                state={
                  incident.severity === "major" ? "major-outage" : "degraded"
                }
              />
            )}
            {!isActive && (
              <span
                style={{ fontSize: 11, color: "var(--muted-foreground)" }}
              >
                resolved
              </span>
            )}
          </div>
          <div
            style={{
              fontSize: 11,
              color: "var(--muted-foreground)",
              marginTop: 4,
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <span>{fmt.datetime(incident.startedAt)}</span>
            <span>·</span>
            <span>
              duration{" "}
              {fmt.duration(incident.startedAt, incident.resolvedAt)}
            </span>
            {incident.components.length > 0 && (
              <>
                <span>·</span>
                <span>{incident.components.join(", ")}</span>
              </>
            )}
          </div>
        </div>
        <Icon
          name={open ? "chevrondown" : "chevron"}
          size={14}
          style={{ color: "var(--muted-foreground)" }}
        />
      </div>

      {open && (
        <div
          style={{
            padding: "4px 16px 16px 32px",
            display: "flex",
            flexDirection: "column",
            gap: 0,
          }}
        >
          {incident.updates.map((u, idx) => (
            <div
              key={`${u.at}-${idx}`}
              style={{
                display: "flex",
                gap: 12,
                position: "relative",
                paddingLeft: 18,
                paddingBottom: 14,
              }}
            >
              {idx < incident.updates.length - 1 && (
                <span
                  style={{
                    position: "absolute",
                    left: 5,
                    top: 14,
                    bottom: 0,
                    width: 1,
                    background: "var(--border)",
                  }}
                />
              )}
              <span
                style={{
                  position: "absolute",
                  left: 0,
                  top: 6,
                  width: 11,
                  height: 11,
                  borderRadius: "50%",
                  background: "var(--card)",
                  border: `2px solid ${STATUS_RING[u.status]}`,
                }}
              />
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 8,
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      color: STATUS_LABEL_COLOR[u.status],
                    }}
                  >
                    {u.status}
                  </span>
                  <span
                    style={{
                      fontSize: 11,
                      color: "var(--muted-foreground)",
                      fontFamily: "var(--font-mono)",
                    }}
                  >
                    {fmt.datetime(u.at)}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: 13,
                    lineHeight: 1.5,
                    marginTop: 4,
                    textWrap: "pretty",
                  }}
                >
                  {u.text}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
